import React, {
  useState,
  useRef,
  useCallback,
  useLayoutEffect,
  useEffect,
  useMemo,
  forwardRef
} from "react";
import { NodeId, NodeOption } from "../types";
import { Connector } from "./CanvasHelpers";
import { ConnectorData, DraggingData } from "../WorkflowsEditor";

interface OptionNodesProps {
  scale: number;
  nodeId: number;
  options: NodeOption[];
  color: string;
  actionType?: string;
  selectedConnector?: HTMLDivElement;
  selectedBranchIndex: number | null;
  draggingData: DraggingData | null;
  renderChildOrEnd: (child: NodeId | undefined) => React.ReactNode;
  onSelectNode: (id: number) => void;
  onSelectBranch: (id: number, branchIndex: number) => void;
  onSelectConnector: (data: ConnectorData) => void;
  onDragOverConnector: (data: ConnectorData) => void;
  onDragLeaveConnector: () => void;
}

export function OptionNodes({
  scale,
  nodeId,
  options,
  color,
  actionType,
  selectedConnector,
  selectedBranchIndex,
  draggingData,
  renderChildOrEnd,
  onSelectNode,
  onSelectBranch,
  onSelectConnector,
  onDragOverConnector,
  onDragLeaveConnector
}: OptionNodesProps) {
  const optionRefs = useRef<HTMLDivElement[]>([]);
  const horizontalBarRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const borderThickness = 4;
  const [horizontalBarWidth, setHorizontalBarWidth] = useState(0);
  const [horizontalBarOffset, setHorizontalBarOffset] = useState(0);

  const calculateHorizontalBarDimensions = useCallback(() => {
    if (!containerRef.current || optionRefs.current.length < 2) return;

    const firstOption = optionRefs.current[0];
    const lastOption = optionRefs.current[options.length - 1];
    if (!firstOption || !lastOption) return;

    // Use getBoundingClientRect for accurate positions relative to container
    // This handles nested content affecting flexbox layout correctly
    const containerRect = containerRef.current.getBoundingClientRect();
    const firstRect = firstOption.getBoundingClientRect();
    const lastRect = lastOption.getBoundingClientRect();

    // Calculate centers relative to container, accounting for scale
    const firstCenter = (firstRect.left - containerRect.left) / scale + (firstRect.width / scale) / 2;
    const lastCenter = (lastRect.left - containerRect.left) / scale + (lastRect.width / scale) / 2;
    const distanceBetweenCenters = lastCenter - firstCenter;

    const width = distanceBetweenCenters + borderThickness;
    setHorizontalBarWidth(width);

    // Offset from container left edge to first option center
    const offset = firstCenter - borderThickness / 2;
    setHorizontalBarOffset(offset);
  }, [options.length, borderThickness, scale]);

  // Serialize options to detect actual structural changes (not reference changes)
  const optionsKey = useMemo(() =>
    options.map(opt => `${opt.childNodeId || 'none'}`).join(','),
    [options]
  );

  // Calculate dimensions after layout
  useLayoutEffect(() => {
    calculateHorizontalBarDimensions();
  }, [calculateHorizontalBarDimensions, optionsKey]);

  // Observe layout changes from nested content (nested conditions, etc.)
  useEffect(() => {
    if (!containerRef.current) return;

    const recalculate = () => {
      // Double RAF ensures browser has completed all layout calculations
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          calculateHorizontalBarDimensions();
        });
      });
    };

    // ResizeObserver detects size changes from nested content
    const resizeObserver = new ResizeObserver(recalculate);

    // MutationObserver detects DOM structure changes
    const mutationObserver = new MutationObserver(recalculate);

    // Observe the container and all current option refs
    resizeObserver.observe(containerRef.current);
    optionRefs.current.forEach((ref) => {
      if (ref) resizeObserver.observe(ref);
    });

    mutationObserver.observe(containerRef.current, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class']
    });

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [calculateHorizontalBarDimensions, options.length]);

  function renderOption(option: NodeOption, i: number) {
    const edgeNode = i === 0 || i === options.length - 1;
    const isSelected = i === selectedBranchIndex;
    // Only show default "Branch X" labels for internal nodes (condition)
    // Ask Question nodes are customer-facing, so leave empty if no label
    const isCustomerFacing = actionType === 'askQuestion';
    const displayText = option.label || (isCustomerFacing ? '' : `Branch ${i + 1}`);
    return (
      <OptionNode
        ref={(ref) => {
          if (ref) {
            optionRefs.current[i] = ref;
          }
        }}
        key={i}
        edgeNode={edgeNode}
        singleNode={options.length === 1}
        text={displayText}
        color={color}
        isSelected={isSelected}
        selectedConnector={selectedConnector}
        draggingData={draggingData}
        onSelect={() => onSelectBranch(nodeId, i)}
        onSelectConnector={(el: HTMLDivElement) =>
          onSelectConnector({
            el,
            parentNodeId: nodeId,
            parentOptionIndex: i,
            childNodeId: option.childNodeId
          })
        }
        onDragOverConnector={(el) =>
          onDragOverConnector({
            el,
            parentNodeId: nodeId,
            parentOptionIndex: i,
            childNodeId: option.childNodeId
          })
        }
        onDragLeaveConnector={onDragLeaveConnector}
        renderChildOrEnd={() => renderChildOrEnd(option.childNodeId)}
      />
    );
  }

  return (
    <>
      <div className="relative h-16 -left-[2px] translate-x-1/2 flex-grow border-l-4 border-slate-300"></div>
      <div ref={containerRef} className="relative">
        {options.length > 1 && (
          <svg
            ref={horizontalBarRef}
            style={{
              left: horizontalBarOffset,
              top: 0
            }}
            width={horizontalBarWidth}
            height="20"
            className="absolute pointer-events-none"
          >
            <path
              d={`M 2,20 L 2,12 Q 2,2 12,2 L ${horizontalBarWidth - 12},2 Q ${horizontalBarWidth - 2},2 ${horizontalBarWidth - 2},12 L ${horizontalBarWidth - 2},20`}
              stroke="#cbd5e1"
              strokeWidth="4"
              fill="none"
              strokeLinecap="square"
            />
          </svg>
        )}
        <div className="relative flex items-start justify-center gap-10">
          {options.map(renderOption)}
        </div>
      </div>
    </>
  );
}

interface OptionNodeProps {
  edgeNode: boolean;
  singleNode: boolean;
  text: string;
  color: string;
  isSelected: boolean;
  selectedConnector?: HTMLDivElement;
  draggingData: DraggingData | null;
  onSelect?: () => void;
  onSelectConnector: (el: HTMLDivElement) => void;
  onDragOverConnector: (el: HTMLDivElement) => void;
  onDragLeaveConnector: () => void;
  renderChildOrEnd: () => React.ReactNode;
}

const OptionNode = forwardRef(
  (
    {
      edgeNode,
      singleNode,
      text,
      color,
      isSelected,
      selectedConnector,
      draggingData,
      onSelect,
      onSelectConnector,
      onDragOverConnector,
      onDragLeaveConnector,
      renderChildOrEnd
    }: OptionNodeProps,
    ref: React.Ref<HTMLDivElement>
  ) => {
    const shadowStyle = isSelected ? "shadow-lg" : "shadow";
    const borderStyle = isSelected ? "ring-2 ring-offset-2 ring-white" : "";

    return (
      <div
        ref={ref}
        className="option flex flex-col items-center justify-between"
      >
        {/* Vertical connector line from horizontal bar to option node */}
        {edgeNode && !singleNode ? (
          <div className="relative h-16 mt-[15px] -left-[2px] translate-x-1/2 flex-grow border-l-4 border-slate-300"></div>
        ) : (
          <div className="relative h-20 -left-[2px] translate-x-1/2 flex-grow border-l-4 border-slate-300"></div>
        )}

        {/* Option node */}
        <div className="flex flex-col items-center justify-center">
          <div
            onClick={onSelect}
            className={`cursor-pointer w-40 h-11 px-4 py-3 ${color} rounded-2xl ${shadowStyle} ${borderStyle} justify-center items-center gap-2 inline-flex`}
          >
            <div className="w-full flex justify-center">
              <div className="text-white text-sm whitespace-nowrap overflow-ellipsis overflow-hidden">
                {text}
              </div>
            </div>
          </div>
        </div>

        {/* Connector and child nodes */}
        <>
          <Connector
            draggingData={draggingData}
            selectedConnector={selectedConnector}
            onClick={onSelectConnector}
            onDragEnter={onDragOverConnector}
            onDragLeave={onDragLeaveConnector}
          />
          {renderChildOrEnd()}
        </>
      </div>
    );
  }
);

OptionNode.displayName = "OptionNode";
